import { useId } from 'react'
import { Botao } from '../../../components/Botao'
import { IconeSeta } from '../../../components/Icones'
import { TEXTOS } from '../../../lib/glossario'

/**
 * Quantidades oferecidas por página.
 *
 * O teto é 100 porque é o teto do servidor (`ChamadoConsultaService.TAMANHO_MAXIMO_PAGINA`), e
 * oferecer 200 numa lista que o servidor corta em 100 seria mentir para quem escolheu. As opções
 * intermediárias existem porque a tarefa muda: conferir uma competência inteira contra o TRP pede
 * página cheia, e olhar o que chegou hoje pede página curta.
 */
const TAMANHOS = [10, 20, 50, 100]

interface Props {
  pagina: number
  totalPaginas: number
  totalElementos: number
  tamanho: number
  aoMudar: (pagina: number) => void
  aoMudarTamanho: (tamanho: number) => void
}

/**
 * Navegação entre páginas da lista de chamados.
 *
 * Mostra "Página X de Y — N chamados atendem a estes filtros", e não só as setas: quem fiscaliza
 * precisa do total para citá-lo no termo de recebimento — no sistema anterior esse número era
 * contado à mão. E o total é o do SERVIDOR, não o das linhas visíveis: a página traz vinte, o
 * recorte pode ter setenta, e a diferença entre esses dois números é a diferença entre conferir e
 * chutar.
 *
 * O total fica em região `aria-live`: quem não vê a tela precisa ouvir que o filtro passou de 64
 * para 7 registros, senão só descobre navegando a lista inteira.
 *
 * A quantidade por página é escolha de quem usa e vive na URL, como todo o resto da triagem: o
 * link que o fiscal cola no e-mail reproduz a tela que ele estava vendo, inclusive o tamanho. Mudar
 * o tamanho volta para a primeira página — quem está na página 4 de 20 em 20 e passa para 100 em
 * 100 pediria uma página que não existe mais.
 *
 * **Por que não `components/Paginacao`.** Aquele componente escreve "solicitações" no meio da
 * frase, fixo. Sobre uma lista de chamados ele diria "64 solicitações no total", que é errado para
 * o público desta tela — solicitação de mudança é outra coisa, e está no mesmo menu. O conserto
 * certo é um substantivo parametrizado lá, com os dois lados usando o mesmo componente; é uma
 * edição pequena e pertence a quem cuida de `components/`. Enquanto ela não acontece, um rodapé
 * próprio é preferível a uma frase errada na tela de quem assina.
 *
 * @param props página atual (base 1), totais, tamanho e o que fazer ao mudar cada um
 * @returns a barra de paginação
 */
export function RodapeDaListagem({ pagina, totalPaginas, totalElementos, tamanho, aoMudar, aoMudarTamanho }: Props) {
  const idDoSeletor = useId()

  // O total aparece SEMPRE — é exatamente com uma página (o filtro apurado do fechamento)
  // que o fiscal mais precisa do número para citar no termo. Só as setas dependem de haver
  // mais de uma página.
  return (
    <nav
      aria-label="Paginação da lista"
      className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4"
    >
      <p className="text-sm text-slate-600" aria-live="polite">
        {totalPaginas > 1 ? `Página ${pagina} de ${totalPaginas} — ` : ''}
        {totalElementos} {totalElementos === 1 ? 'chamado atende' : 'chamados atendem'} a estes filtros
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor={idDoSeletor} className="text-sm text-slate-600">
            {TEXTOS.chamados.itensPorPagina}
          </label>
          <select
            id={idDoSeletor}
            value={tamanho}
            onChange={(evento) => aoMudarTamanho(Number(evento.target.value))}
            className={
              'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-800 ' +
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
              'focus-visible:outline-niteroi-600'
            }
          >
            {/* Um tamanho vindo de URL editada à mão não pode sumir do seletor: sem esta opção, o
                campo mostraria 20 enquanto a lista traz 35, e a tela estaria mentindo. */}
            {(TAMANHOS.includes(tamanho) ? TAMANHOS : [...TAMANHOS, tamanho].sort((a, b) => a - b)).map((valor) => (
              <option key={valor} value={valor}>
                {valor}
              </option>
            ))}
          </select>
        </div>

        {totalPaginas > 1 && (
          <div className="flex gap-2">
            <Botao tamanho="pequeno" onClick={() => aoMudar(pagina - 1)} disabled={pagina <= 1}>
              <IconeSeta className="h-4 w-4 rotate-180" />
              Anterior
            </Botao>
            <Botao tamanho="pequeno" onClick={() => aoMudar(pagina + 1)} disabled={pagina >= totalPaginas}>
              Próxima
              <IconeSeta className="h-4 w-4" />
            </Botao>
          </div>
        )}
      </div>
    </nav>
  )
}
